/** SUNTRONIX ONE — CRM Opportunities (Phase 2). Internal-only. */
import { orgLabel } from "@/lib/orgLabel";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Target, Plus, RefreshCw, ChevronLeft, CheckCircle2, TrendingUp,
  CloudDownload, DollarSign,
} from "lucide-react";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

const STAGES = [
  "Prospecting", "Qualification", "Needs Analysis", "Value Proposition",
  "Proposal/Price Quote", "Negotiation/Review", "Closed Won", "Closed Lost",
];

const STAGE_TONE = {
  "Prospecting":          "bg-slate-100 text-slate-700 border-slate-200",
  "Qualification":        "bg-blue-50 text-blue-800 border-blue-200",
  "Needs Analysis":       "bg-indigo-50 text-indigo-800 border-indigo-200",
  "Value Proposition":    "bg-purple-50 text-purple-800 border-purple-200",
  "Proposal/Price Quote": "bg-amber-50 text-amber-800 border-amber-200",
  "Negotiation/Review":   "bg-orange-50 text-orange-800 border-orange-200",
  "Closed Won":            "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Closed Lost":           "bg-rose-50 text-rose-700 border-rose-200",
};


export default function OpportunitiesList() {
  const [rows, setRows] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [oppsRes, pipRes] = await Promise.all([
        api.get("/vtiger/opportunities"),
        api.get("/vtiger/opportunities/pipeline"),
      ]);
      setRows(oppsRes.data.items || []);
      setPipeline(pipRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load opportunities");
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);


  const filtered = stageFilter === "all" ? rows
    : rows.filter((r) => r.stage === stageFilter);

  return (
    <div className="space-y-5 max-w-[1600px]" data-testid="crm-opportunities-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/commercial/crm" className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Commercial Workspace
          </Link>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-1 flex items-center gap-2">
            <Target className="h-6 w-6 text-emerald-700" />
            Opportunities
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Deal pipeline — forecasts weighted by stage probability.
          </p>
        </div>
        <div className="flex gap-2">
                    <Button variant="outline" onClick={load} disabled={busy}>
            <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} /> Refresh
          </Button>
          <Button onClick={() => setShowNew(true)}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="opp-new">
            <Plus className="h-4 w-4 mr-1.5" /> New opportunity
          </Button>
        </div>
      </div>

      {/* Pipeline funnel */}
      {pipeline && (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-700" />
              Pipeline Funnel
            </h2>
            <div className="text-right">
              <div className="text-[10px] uppercase text-slate-500">Weighted forecast</div>
              <div className="text-xl font-heading font-semibold text-emerald-700"
                data-testid="weighted-forecast">
                {fmtINR(pipeline.total_weighted_inr)}
              </div>
              <div className="text-[10px] text-slate-500">
                Raw total {fmtINR(pipeline.total_amount_inr)}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            {pipeline.stages.map((s) => {
              const pct = pipeline.total_amount_inr
                ? (s.amount_inr / pipeline.total_amount_inr) * 100 : 0;
              return (
                <button key={s.stage} onClick={() => setStageFilter(s.stage)}
                  data-testid={`funnel-${s.stage.replace(/[^A-Za-z]/g, "-")}`}
                  className="w-full text-left flex items-center gap-3 hover:bg-slate-50 rounded px-2 py-1">
                  <div className="w-40 flex items-center gap-1.5">
                    <Badge className={"text-[10px] border " + STAGE_TONE[s.stage]}>
                      {s.stage}
                    </Badge>
                  </div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full"
                      style={{ width: Math.max(2, Math.min(100, pct)) + "%" }} />
                  </div>
                  <div className="w-24 text-right text-[11px] text-slate-500">
                    {s.count} × <span className="text-slate-900 font-mono">{fmtINR(s.amount_inr)}</span>
                  </div>
                  <div className="w-20 text-right text-[10px] text-emerald-700">
                    @ {s.probability_pct}%
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-md p-3 flex gap-2 items-center flex-wrap">
        <button onClick={() => setStageFilter("all")}
          className={"h-8 px-3 rounded text-[12px] font-medium " +
            (stageFilter === "all" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")}
          data-testid="opp-filter-all">
          All
        </button>
        {STAGES.map((s) => (
          <button key={s} onClick={() => setStageFilter(s)}
            data-testid={`opp-filter-${s.replace(/[^A-Za-z]/g, "-")}`}
            className={"h-8 px-3 rounded text-[11px] font-medium " +
              (stageFilter === s ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")}>
            {s}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-slate-500">{filtered.length} row(s)</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {filtered.length === 0 && (
          <div className="p-10 text-center text-[13px] text-slate-500">
            No opportunities in this stage yet.
          </div>
        )}
        {filtered.map((o) => (
          <button key={o.id} onClick={() => setSelected(o)}
            data-testid={`opp-row-${o.id}`}
            className="w-full text-left p-4 hover:bg-slate-50 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[13px] text-slate-900">{o.opportunity_name}</span>
                <Badge className={"text-[10px] border " + (STAGE_TONE[o.stage] || "")}>{o.stage}</Badge>
                
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {o.expected_close_date ? `Close ${new Date(o.expected_close_date).toLocaleDateString()}` : "No close date"} · @ {o.probability_pct}%
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-slate-500">Amount</div>
              <div className="text-[15px] font-heading font-semibold">{fmtINR(o.amount_inr)}</div>
              <div className="text-[10px] text-emerald-700">
                Weighted {fmtINR(o.weighted_amount_inr)}
              </div>
            </div>
          </button>
        ))}
      </div>

      {showNew && (
        <NewOppDrawer onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }} />
      )}
      {selected && (
        <OppDrawer opp={selected} onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}


function NewOppDrawer({ onClose, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    customer_master_company_id: "",
    account_id: "", opportunity_name: "",
    amount_inr: 0, stage: "Prospecting",
    expected_close_date: "", description: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/master-companies").then(({ data }) => setCustomers(data.items || []));
    api.get("/vtiger/accounts").then(({ data }) => setAccounts(data.items || []));
  }, []);

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.customer_master_company_id || !form.opportunity_name) {
      toast.error("Customer and opportunity name are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/vtiger/opportunities", {
        ...form, amount_inr: Number(form.amount_inr || 0),
      });
      toast.success("Opportunity created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}
      data-testid="new-opp-drawer">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-heading font-semibold">New Opportunity</h2>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>
        <div className="mt-4 space-y-3">
          <Field label="Customer *">
            <select value={form.customer_master_company_id}
              onChange={(e) => upd("customer_master_company_id", e.target.value)}
              className="w-full h-9 border border-slate-300 rounded-md text-[13px] px-2"
              data-testid="new-opp-customer">
              <option value="">— Select —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{orgLabel(c, customers)}</option>)}
            </select>
          </Field>
          <Field label="Legacy account (optional)">
            <select value={form.account_id}
              onChange={(e) => upd("account_id", e.target.value)}
              className="w-full h-9 border border-slate-300 rounded-md text-[13px] px-2"
              data-testid="new-opp-account">
              <option value="">— None —</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.account_name}</option>)}
            </select>
          </Field>
          <Field label="Opportunity name *">
            <Input value={form.opportunity_name}
              onChange={(e) => upd("opportunity_name", e.target.value)}
              placeholder="e.g. Q1 2026 Laptop Refresh"
              data-testid="new-opp-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (INR)">
              <Input type="number" value={form.amount_inr}
                onChange={(e) => upd("amount_inr", e.target.value)}
                data-testid="new-opp-amount" />
            </Field>
            <Field label="Expected close date">
              <Input type="date" value={form.expected_close_date}
                onChange={(e) => upd("expected_close_date", e.target.value)}
                data-testid="new-opp-close-date" />
            </Field>
          </div>
          <Field label="Stage">
            <select value={form.stage} onChange={(e) => upd("stage", e.target.value)}
              className="w-full h-9 border border-slate-300 rounded-md text-[13px] px-2"
              data-testid="new-opp-stage">
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Description">
            <Textarea rows={3} value={form.description}
              onChange={(e) => upd("description", e.target.value)}
              data-testid="new-opp-description" />
          </Field>
        </div>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className="flex-1 h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="new-opp-submit">
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            {busy ? "Creating…" : "Create opportunity"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function OppDrawer({ opp, onClose, onSaved }) {
  const [stage, setStage] = useState(opp.stage);
  const [amount, setAmount] = useState(opp.amount_inr);
  const [description, setDescription] = useState(opp.description || "");
  const [expectedCloseDate, setExpectedCloseDate] = useState(opp.expected_close_date || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/vtiger/opportunities/${opp.id}`, {
        stage, amount_inr: Number(amount), description,
        expected_close_date: expectedCloseDate || null,
      });
      toast.success("Opportunity updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}
      data-testid="opp-drawer">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading font-semibold">{opp.opportunity_name}</h2>
            <div className="flex gap-2 mt-2">
              <Badge className={"text-[10px] border " + (STAGE_TONE[opp.stage] || "")}>{opp.stage}</Badge>
              
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <InfoTile label="Amount" value={fmtINR(opp.amount_inr)} bold />
          <InfoTile label="Weighted (@ prob)" value={fmtINR(opp.weighted_amount_inr)} tone="ok" />
        </div>
        <div className="mt-4 space-y-3">
          <Field label="Stage">
            <select value={stage} onChange={(e) => setStage(e.target.value)}
              className="w-full h-9 border border-slate-300 rounded-md text-[13px] px-2"
              data-testid="opp-stage-select">
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount">
              <Input type="number" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="opp-amount" />
            </Field>
            <Field label="Close date">
              <Input type="date" value={expectedCloseDate}
                onChange={(e) => setExpectedCloseDate(e.target.value)}
                data-testid="opp-close-date" />
            </Field>
          </div>
          <Field label="Description">
            <Textarea rows={3} value={description}
              onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </div>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} className="ml-auto">Cancel</Button>
          <Button onClick={save} disabled={busy}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="opp-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function InfoTile({ label, value, bold, tone }) {
  const tint = tone === "ok" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="border border-slate-200 rounded-md p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[14px] mt-0.5 ${tint} ${bold ? "font-semibold font-mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}
